import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Pagination,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircleOutline as CheckIcon,
  FlagOutlined as FlagIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import reviewsService, {
  ReviewReport,
  ReviewReportStats,
  ReviewReportStatus,
} from '../services/reviewsService';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

/**
 * صف رسیدگی به گزارش‌های تخلفِ کاربران روی «دیدگاه محصول».
 *
 * وجودِ این صف شرطِ انتشار در کافه‌بازار است: قوانین استور برای محتوای
 * کاربرساخته سازوکار گزارش‌دهی و رسیدگی می‌خواهند.
 */

const STATUS_TABS: Array<{ value: ReviewReportStatus | ''; label: string }> = [
  { value: 'pending', label: 'در انتظار رسیدگی' },
  { value: 'actioned', label: 'حذف‌شده' },
  { value: 'dismissed', label: 'رد گزارش' },
  { value: '', label: 'همه' },
];

const STATUS_META: Record<ReviewReportStatus, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
  pending: { label: 'در انتظار', color: 'warning' },
  reviewed: { label: 'بررسی‌شده', color: 'default' },
  dismissed: { label: 'رد گزارش', color: 'success' },
  actioned: { label: 'دیدگاه حذف شد', color: 'error' },
};

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  try {
    return toPersianDigits(
      new Date(iso).toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    );
  } catch {
    return '—';
  }
};

const ReviewReportsManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<ReviewReportStatus | ''>('pending');
  const [reports, setReports] = useState<ReviewReport[]>([]);
  const [stats, setStats] = useState<ReviewReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [target, setTarget] = useState<{ report: ReviewReport; action: 'dismiss' | 'hide' } | null>(
    null
  );
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, s] = await Promise.all([
        reviewsService.getReports(page, 20, { status }),
        reviewsService.getReportStats(),
      ]);
      setReports(data.reports);
      setPages(data.pagination.pages || 1);
      setStats(s);
    } catch {
      enqueueSnackbar('خطا در دریافت گزارش‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, status, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await reviewsService.reviewReport(target.report._id, target.action, note.trim() || undefined);
      enqueueSnackbar(
        target.action === 'dismiss' ? 'گزارش رد شد' : 'دیدگاه از دید عمومی خارج شد',
        { variant: target.action === 'dismiss' ? 'success' : 'warning' }
      );
      setTarget(null);
      setNote('');
      load();
    } catch {
      enqueueSnackbar('خطا در رسیدگی به گزارش', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            گزارش‌های تخلف دیدگاه‌ها
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            دیدگاه‌هایی که کاربران به‌عنوان محتوای نامناسب گزارش کرده‌اند.
          </Typography>
        </Box>
        <Button onClick={load} startIcon={<RefreshIcon />} variant="outlined" size="small">
          بروزرسانی
        </Button>
      </Stack>

      {stats && (
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
          <Chip color="warning" label={`در انتظار: ${toPersianDigits(String(stats.pending))}`} />
          <Chip color="error" label={`حذف‌شده: ${toPersianDigits(String(stats.actioned))}`} />
          <Chip color="success" label={`رد گزارش: ${toPersianDigits(String(stats.dismissed))}`} />
          <Chip variant="outlined" label={`کل: ${toPersianDigits(String(stats.total))}`} />
        </Stack>
      )}

      <Tabs
        value={status}
        onChange={(_, v) => {
          setStatus(v);
          setPage(1);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {STATUS_TABS.map((t) => (
          <Tab key={t.value || 'all'} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Alert severity="success" icon={<CheckIcon />}>
          گزارشی در این دسته وجود ندارد.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {reports.map((r) => {
            const meta = STATUS_META[r.status];
            const reporter = [r.reporterId?.firstName, r.reporterId?.lastName]
              .filter(Boolean)
              .join(' ');
            const author =
              [r.reviewId?.userId?.firstName, r.reviewId?.userId?.lastName]
                .filter(Boolean)
                .join(' ') || r.snapshot?.authorName;
            const comment = r.reviewId?.comment || r.snapshot?.comment;

            return (
              <Card key={r._id} variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                    <FlagIcon color="error" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {r.reasonLabel || r.reason}
                    </Typography>
                    <Chip size="small" label={meta.label} color={meta.color} />
                    {!!r.reviewId?.reportCount && r.reviewId.reportCount > 1 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="error"
                        label={`${toPersianDigits(String(r.reviewId.reportCount))} گزارش`}
                      />
                    )}
                    {r.reviewId && r.reviewId.isApproved === false && (
                      <Chip size="small" variant="outlined" label="در حال حاضر مخفی است" />
                    )}
                    <Box flex={1} />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(r.createdAt)}
                    </Typography>
                  </Stack>

                  {r.description && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      توضیح گزارش‌دهنده: {r.description}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      p: 1.5,
                      borderRight: 3,
                      borderColor: 'error.light',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {comment || '— متن دیدگاه در دسترس نیست —'}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary">
                      نویسندهٔ دیدگاه: <strong>{author || '—'}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      گزارش‌دهنده: <strong>{reporter || '—'}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      محصول: <strong>{r.productId?.name || r.snapshot?.productName || '—'}</strong>
                    </Typography>
                  </Stack>

                  {r.adminNote && (
                    <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.82rem' }}>
                      یادداشت ادمین: {r.adminNote}
                    </Alert>
                  )}

                  {r.status === 'pending' && (
                    <Stack direction="row" spacing={1} mt={1.5}>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<BlockIcon />}
                        onClick={() => {
                          setTarget({ report: r, action: 'hide' });
                          setNote('');
                        }}
                      >
                        حذف دیدگاه از سایت
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CheckIcon />}
                        onClick={() => {
                          setTarget({ report: r, action: 'dismiss' });
                          setNote('');
                        }}
                      >
                        گزارش بی‌مورد است
                      </Button>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {pages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      <Dialog open={!!target} onClose={() => setTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {target?.action === 'hide' ? 'حذف دیدگاه از دید عمومی' : 'رد کردن گزارش'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {target?.action === 'hide'
              ? 'دیدگاه دیگر در سایت و اپ نمایش داده نمی‌شود. متن دیدگاه حذف نمی‌شود و در پنل قابل بازگردانی است.'
              : 'گزارش بی‌مورد علامت می‌خورد. اگر دیدگاه فقط به‌خاطر همین گزارش‌ها خودکار مخفی شده بود، دوباره منتشر می‌شود.'}
          </Typography>
          <TextField
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            rows={2}
            label="یادداشت (اختیاری)"
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>انصراف</Button>
          <Button
            onClick={submit}
            variant="contained"
            color={target?.action === 'hide' ? 'error' : 'primary'}
            disabled={saving}
          >
            {saving ? 'در حال ثبت...' : 'تایید'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewReportsManagement;
